import {
  ACCEPTED_SHAPEFILES,
  MAX_SHAPEFILE_SIZE,
  MpxResponseError,
  UploadLandAPI,
} from '@cibo/core'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { FileWithPath } from 'file-selector'
import { MouseEvent, useCallback, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

export const useUploadGeometry = (
  resourceId: string,
  onBusy: (arg: boolean) => void,
  onError: (arg: any) => void,
  onUpload: (arg: any) => void,
  onFilesRejected?: (rejections: FileRejection[]) => void
) => {
  const { t } = useTranslation('uploadShapefileForm')

  const [busy, setBusy] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [error, setError] = useState<AxiosError<MpxResponseError> | undefined>()
  const [files, setFiles] = useState<File[] | undefined>(undefined)
  const [acceptedFiles, setAcceptedFiles] = useState<FileWithPath[]>()
  const [rejections, setRejections] = useState<FileRejection[]>()
  const [excludedFiles, setExcludedFiles] = useState<string[] | undefined>()
  const queryClient = useQueryClient()

  const onDrop = useCallback((acceptedFiles: File[], rejections: FileRejection[]) => {
    setError(undefined)

    setAcceptedFiles(acceptedFiles)
    setRejections(rejections)
    if (rejections.length && onFilesRejected) {
      onFilesRejected(rejections)
    }

    setExcludedFiles([])

    if (acceptedFiles.length > 0) {
      setErrorMessage(undefined)
      setFiles(acceptedFiles)
    } else {
      setErrorMessage(
        t('rejectedFile', {
          count: rejections.length,
          name: rejections.map(({ file: { name } }) => name).join(', '),
        })
      )
      setFiles(undefined)
    }
  }, [])

  const dropzone = useDropzone({
    onDrop,
    accept: ACCEPTED_SHAPEFILES,
    maxSize: MAX_SHAPEFILE_SIZE,
    multiple: false,
    noClick: true,
  })

  const excludeFile = (filename: string) => {
    setError(undefined)
    setExcludedFiles(excludedFiles ? [...excludedFiles, filename] : [filename])
  }

  const includedAcceptedFiles: FileWithPath[] = []
  if (acceptedFiles) {
    for (let i = 0, l = acceptedFiles.length; i < l; i++) {
      const file = acceptedFiles[i]
      if (!excludedFiles || !excludedFiles.includes(file.name)) {
        includedAcceptedFiles.push(file)
      }
    }
  }

  const handleUpload = async (e: MouseEvent<HTMLElement>, fieldName?: string) => {
    if (!includedAcceptedFiles.length) {
      return
    }

    const formData = new FormData()

    includedAcceptedFiles.forEach(file => {
      formData.append('file', file)
    })

    if (fieldName) {
      formData.append('name', fieldName)
    }

    onBusy(true)
    setBusy(true)

    return UploadLandAPI.uploadSingleField(formData, resourceId)
      .then((data: any) => {
        // 'batchedFields' below s/b BATCHED_FIELD_QUERY_KEY but this package doesn't import landmanager
        queryClient.invalidateQueries({ queryKey: ['batchedFields', resourceId] })
        onUpload && onUpload(data)
      })
      .catch(error => {
        onError(error)
        setError(error)
      })
      .finally(() => {
        onBusy(false)
        setBusy(false)
      })
  }

  return {
    acceptedFiles,
    rejections,
    excludedFiles,
    busy,
    dropzone,
    error,
    errorMessage,
    files,
    handleUpload,
    excludeFile,
    includedAcceptedFiles,
  }
}
